import { Component, Output, EventEmitter, OnInit, Input, OnChanges } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { AreaPropertyOption } from '@models/area-property-option.model';
import { AreaProperty } from '@models/area-property.model';
import { InputType, InputTypeLabels } from '@models/input-type.enum';
import { StorageService } from '@services/storage.service';
import { ToastService } from '@shared/services/toast.service';
import { v4 as uuidv4 } from 'uuid';

@Component({
  standalone: false,
  selector: 'app-area-properties-form',
  templateUrl: './area-properties-form.component.html',
  styleUrls: ['./area-properties-form.component.scss'],
})
export class AreaPropertiesFormComponent implements OnInit, OnChanges {
  @Input() areaId!: string;
  @Input() properties: AreaProperty[] = [];
  @Input() editingProperty: AreaProperty | null = null;

  @Output() propertyAdded = new EventEmitter<AreaProperty>();
  @Output() propertyUpdated = new EventEmitter<AreaProperty>();

  propertyForm!: FormGroup;
  propertyTypes = Object.values(InputType);
  inputTypeLabels = InputTypeLabels;

  constructor(
    private fb: FormBuilder,
    private toast: ToastService,
    private storageService: StorageService
  ) { }

  ngOnInit(): void {
    this.propertyForm = this.fb.group({
      id: uuidv4(),
      areaId: this.areaId,
      parentId: null,
      keyName: [{ value: '', disabled: true }, Validators.required],
      label: ['', Validators.required],
      inputType: [this.propertyTypes[0], Validators.required],
      unit: [''],
      isRequired: [false],
      orderIndex: [0],
      value: [''],
      options: this.fb.array([]),
      children: this.fb.array([]),
    });
  }

  ngOnChanges(): void {
    if (this.editingProperty) {
      this.propertyForm.patchValue(this.editingProperty);
      if (this.editingProperty?.options) {
        this.options.clear();
        this.editingProperty.options.forEach(opt => {
          this.options.push(this.fb.control(opt));
        });
      }

    } else {
      this.resetForm(false);
    }
  }

  save(): void {
    if (this.propertyForm.invalid) {
      this.toast.errorToast('Completa los campos obligatorios.');
      this.propertyForm.markAllAsTouched();
      return;
    }

    const formValue = { ...this.propertyForm.getRawValue() };

    // generar keyName si no existe
    if (!formValue.keyName) {
      const randomSuffix = Math.floor(Math.random() * 1000);
      const datePart = new Date().toISOString().slice(0, 10).replace(/-/g, '');

      formValue.keyName = formValue.label
        .toLowerCase()
        .replace(/\s+/g, '_')
        .replace(/[^\w_]/g, '') +
        `_${datePart}_${randomSuffix}`;
    }

    const property = formValue as AreaProperty;

    // 🔥 Diferenciar entre editar o añadir
    if (this.editingProperty) {
      this.propertyUpdated.emit(property);
      this.toast.successToast('Propiedad actualizada');
    } else {
      this.propertyAdded.emit(property);
      this.toast.successToast('Propiedad creada');
    }

    this.resetForm();
  }

  resetForm(showToast: boolean = true): void {
    if (!this.propertyForm) return;   // ⛔ Evita el error si el form aún no existe

    this.propertyForm.reset({
      id: uuidv4(),
      areaId: this.areaId,
      parentId: null,
      keyName: '',
      label: '',
      inputType: this.propertyTypes[0],
      unit: '',
      isRequired: false,
      orderIndex: 0,
      value: '',
      options: [],
      children: [],
    });

    if (showToast) {
      this.toast.successToast('Formulario listo para nueva propiedad.');
    }
  }

  onParentChange(event: any): void {
    const selectedParent = this.properties.find(p => p.id === event.value);
    if (selectedParent) {
      this.propertyForm.patchValue({ parentId: selectedParent.id });
    } else {
      this.propertyForm.patchValue({ parentId: null });
    }
  }


  //OPTIONS List

  onInputTypeChange(event: any) {
    const type = event.value;

    if (type !== InputType.LIST) {
      this.options.clear();
    }
    // Reiniciar el valor según el tipo
    if (type === InputType.BOOLEAN) {
      this.propertyForm.patchValue({ value: false });
    } else if (type === InputType.NUMBER) {
      this.propertyForm.patchValue({ value: null });
    } else if (type === InputType.DATE) {
      this.propertyForm.patchValue({ value:null });
    } else if (type === InputType.LOCATION || type === InputType.GEOPOLYGON) {
      // Sin valor por defecto: el punto/parcela se define en la instancia, no en la plantilla.
      this.propertyForm.patchValue({ value: null });
    } else {
      // text, list, etc.
      this.propertyForm.patchValue({ value: '' });
    }
  }


  get options(): FormArray {
    return this.propertyForm.get('options') as FormArray;
  }

  addOption(value: string) {
    if (!value.trim()) return;

    const option: AreaPropertyOption = {
      id: uuidv4(),
      areaPropertyId: this.propertyForm.get('id')?.value,
      value,
      label: value
    };

    this.options.push(new FormControl(option));
  }

  deleteOption(index: number) {
    this.options.removeAt(index);
  }

}
